// "3D-Fenster" im Drucken-Modal: frei platzierbare/grössenveränderbare
// Rechtecke auf dem Druckblatt, die eine 3D-Ansicht des Projekts zeigen.
//
// Architektur (siehe Plan /home/beno/.claude/plans/nested-dazzling-turtle.md):
// - Nur EIN Fenster ist je gleichzeitig "live" (interaktiv, eigener Renderer/
//   Kamera/OrbitControls/ViewCube) — alle anderen zeigen ein eingefrorenes
//   PNG-Snapshot-Bild. Browser begrenzen gleichzeitige WebGL-Kontexte
//   ohnehin, und die Spec selbst erlaubt nur ein Fenster im Dreh-Modus.
// - Das Live-Fenster rendert die GETEILTE S.view3d.scene (kein Klonen) mit
//   einem eigenen Renderer/Kamera/OrbitControls-Satz — Three.js erlaubt das
//   nativ (mehrere Kamera/Renderer-Paare auf einer Szene, z.B. Splitscreen).
// - Eigener Renderer/Scene/Kamera/OrbitControls/ViewCube, eigener rAF-Loop,
//   sauberes dispose().
import { S } from '../app/app_state.js?v=20260817c';
import { escapeHtml } from '../utils/html_utils.js?v=20260706d';
import { t } from '../i18n/i18n_service.js?v=20260722a';
import { makeId } from '../utils/id_service.js?v=20260706d';
import { normalizeOutdoorEnvironment, normalizeOutdoorSunBrightness } from '../three/outdoor_environment_service.js';
import { OUTDOOR_SUN_TIMES, isValidOutdoorSunTime } from '../three/outdoor_sun_service.js';
import { maxWebGLRenderEdgePx, releaseWebGLRenderer } from '../render/webgl_renderer_lifecycle.js?v=20260807a';
import { collectReferenceLines, updateAxisSnap } from './print_plan_window_snap.js?v=20260803a';
import {
  applyPrintPlanSceneGroupFilter,
  selectedPrintPlanGroupBounds
} from './print_plan_group_filter.js?v=20260904c';
import {
  normalizePrintPlanGroupKeys,
  printPlanGroupSelectionSignature
} from './print_plan_group_filter_model.mjs?v=20260904c';

// Pure data/constant modules (no Three.js dependency, verified) — safe to
// import statically without pulling the 3D engine into the print modal's
// initial bundle. The 3D engine itself (scene3d.js etc.) stays behind
// load3DModules()'s dynamic import below.

// The 3D engine (scene3d.js, three_controls.js, viewcube.js) is only ever
// needed once the user actually adds/orbits/prints a 3D window — dynamically
// imported (and cached) here rather than statically, so simply opening the
// print modal never pulls in the whole Three.js engine bundle.
let modulesPromise = null;
function load3DModules() {
  if (!modulesPromise) {
    modulesPromise = Promise.all([
      import('../three/scene3d.js?v=20260904f'),
      import('../three/three_controls.js?v=20260812b'),
      import('../viewport/viewcube.js?v=20260706d'),
      import('../export/snapshot_controller.js?v=20260904f')
    ]).then(([scene3d, threeControls, viewcube, snapshotController]) => ({ scene3d, threeControls, viewcube, snapshotController }));
  }
  return modulesPromise;
}

const MIN_WINDOW_MM = 20;
const DEFAULT_WINDOW_WIDTH_MM = 90;
const DEFAULT_WINDOW_HEIGHT_MM = 60;
const PLACEHOLDER_SNAPSHOT_MAX_EDGE_PX = 420;
const DEFAULT_THETA = -0.65;
const DEFAULT_PHI = 0.9;

// Per-window Ebenen/Beleuchtung — same 4 preset IDs and 4 visibility-filter
// keys as the app's own global Ebenen system (scene3d.js/environment_presets.js),
// duplicated here rather than imported as an ordered list because the display
// ORDER the user asked for differs from the main app's own button order
// (Boden/Doppelboden/Decke/Doppeldecke here vs. Boden/Doppelboden/Doppeldecke/
// Decke there) — same accepted duplication pattern already used elsewhere in
// scene3d.js itself (the preset-ID list is hardcoded in three separate places).
const WINDOW_PRESET_IDS = ['outdoor', 'datacenter', 'basement', 'custom'];
const WINDOW_FILTER_KEYS = ['floor', 'doubleFloor', 'ceiling', 'doubleCeiling'];

function presetLabel(id) {
  switch (id) {
    case 'outdoor': return t('modal.drucken.wert.preset_aussenbereich', 'Aussenbereich');
    case 'datacenter': return t('modal.drucken.wert.preset_datacenter', 'Datacenter');
    case 'basement': return t('modal.drucken.wert.preset_innenbereich', 'Innenbereich');
    case 'custom': return t('modal.drucken.wert.preset_individuell', 'Individuell');
    default: return id;
  }
}

function filterLabel(key) {
  switch (key) {
    case 'floor': return t('modal.drucken.wert.filter_boden', 'Boden');
    case 'doubleFloor': return t('modal.drucken.wert.filter_doppelboden', 'Doppelboden');
    case 'ceiling': return t('modal.drucken.wert.filter_decke', 'Decke');
    case 'doubleCeiling': return t('modal.drucken.wert.filter_doppeldecke', 'Doppeldecke');
    default: return key;
  }
}

let layerEl = null;
let windowsState = [];
let selectedWindowId = null;
let lastFitScale = 1;
let lastFrameMm = { width: 0, height: 0, borderMm: 0 };
let lastTitleBlockGeometry = null;
let snapGuideVerticalEl = null;
let snapGuideHorizontalEl = null;
let liveInstance = null;
let onGeometryCommit = null;
let onSidebarRefreshNeeded = null;
let onBeforeSelect = null;
let selectedPrintGroupKeys = null;
let selectedPrintGroupSignature = '*';
let viewWindowLifecycleGeneration = 0;

// ---------------------------------------------------------------------------
// Data model
// ---------------------------------------------------------------------------

function normalizeVec3(value) {
  if (!value || typeof value !== 'object') return null;
  const x = Number(value.x);
  const y = Number(value.y);
  const z = Number(value.z);
  if (![x, y, z].every(Number.isFinite)) return null;
  return { x, y, z };
}

// null means "not yet defaulted" — resolved lazily on first actual render,
// see resolveWindowSceneFilters(). Kept separate from camera/snapshot
// normalization since it has its own set of valid enum values to check.
function normalizeSceneFilters(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const preset = WINDOW_PRESET_IDS.includes(raw.preset) ? raw.preset : null;
  if (!preset) return null;
  const f = raw.filters && typeof raw.filters === 'object' ? raw.filters : {};
  return {
    preset,
    filters: {
      floor: f.floor !== false,
      doubleFloor: f.doubleFloor !== false,
      doubleCeiling: f.doubleCeiling !== false,
      ceiling: f.ceiling === true
    },
    brightness: normalizeOutdoorSunBrightness(raw.brightness),
    sunTime: isValidOutdoorSunTime(raw.sunTime) ? raw.sunTime : '12:00'
  };
}

function normalizeViewWindow(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const num = (value, fallback) => (Number.isFinite(Number(value)) ? Number(value) : fallback);
  return {
    id: typeof raw.id === 'string' && raw.id ? raw.id : makeId('viewwindow'),
    type: '3d',
    xMm: num(raw.xMm, 10),
    zMm: num(raw.zMm, 10),
    widthMm: Math.max(MIN_WINDOW_MM, num(raw.widthMm, DEFAULT_WINDOW_WIDTH_MM)),
    heightMm: Math.max(MIN_WINDOW_MM, num(raw.heightMm, DEFAULT_WINDOW_HEIGHT_MM)),
    camera: {
      projectionMode: raw.camera?.projectionMode === 'orthographic' ? 'orthographic' : 'perspective',
      position: normalizeVec3(raw.camera?.position),
      target: normalizeVec3(raw.camera?.target),
      orthographicViewHeight: Number.isFinite(Number(raw.camera?.orthographicViewHeight))
        ? Number(raw.camera.orthographicViewHeight)
        : null
    },
    snapshot: typeof raw.snapshot === 'string' && raw.snapshot.startsWith('data:image/') ? raw.snapshot : null,
    snapshotUpdatedAt: Number.isFinite(Number(raw.snapshotUpdatedAt)) ? Number(raw.snapshotUpdatedAt) : null,
    snapshotGroupSelectionSignature: typeof raw.snapshotGroupSelectionSignature === 'string'
      ? raw.snapshotGroupSelectionSignature
      : null,
    sceneFilters: normalizeSceneFilters(raw.sceneFilters)
  };
}

// type is undefined for windows saved before the 2D-window feature existed —
// treated as legacy 3D for backward compatibility. Now that print_plan_2d_views.js
// entries (type:'2d') share the same combined viewWindows array (see
// commitPrintSettings/openPrintPlanModal in print_plan_modal.js), those MUST be
// filtered out here, or they'd silently get miscoerced into malformed 3D windows
// (normalizeViewWindow always hardcodes type:'3d' on its own output).
function normalizeViewWindows(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.filter(entry => entry?.type !== '2d').map(normalizeViewWindow).filter(Boolean);
}

function serializeViewWindows() {
  return windowsState.map(win => ({
    id: win.id,
    type: win.type,
    xMm: win.xMm,
    zMm: win.zMm,
    widthMm: win.widthMm,
    heightMm: win.heightMm,
    camera: { ...win.camera },
    snapshot: win.snapshot,
    snapshotUpdatedAt: win.snapshotUpdatedAt,
    snapshotGroupSelectionSignature: win.snapshotGroupSelectionSignature,
    sceneFilters: win.sceneFilters ? { ...win.sceneFilters, filters: { ...win.sceneFilters.filters } } : null
  }));
}

function currentPrintGroupSelection() {
  return {
    selectedGroupKeys: selectedPrintGroupKeys === null ? null : [...selectedPrintGroupKeys],
    signature: selectedPrintGroupSignature
  };
}

function snapshotMatchesPrintGroupSelection(win) {
  if (!win?.snapshot) return false;
  if (win.snapshotGroupSelectionSignature === selectedPrintGroupSignature) return true;
  return !win.snapshotGroupSelectionSignature && selectedPrintGroupSignature === '*';
}

function createViewWindow() {
  const cascade = windowsState.length * 10;
  return normalizeViewWindow({
    xMm: 10 + (cascade % 100),
    zMm: 10 + (cascade % 60),
    widthMm: DEFAULT_WINDOW_WIDTH_MM,
    heightMm: DEFAULT_WINDOW_HEIGHT_MM
  });
}

// ---------------------------------------------------------------------------
// Sidebar card
// ---------------------------------------------------------------------------

// extraButtonsHtml: additional action buttons print_plan_modal.js wants in the
// same options row (2D/Detail/Planansicht). extraSettingsHtml: another
// module's selected-window settings panel (currently the 2D window's
// direction grid + filters) — appended alongside this module's own, since
// cross-module deselect coordination (onBeforeSelect) guarantees at most one
// of the two is ever non-empty at a time. Kept as injection points rather
// than moving the whole card's ownership, since the two window types'
// settings panels are structurally different enough that a shared owner
// wouldn't simplify much.
function viewsSidebarCardHtml(extraButtonsHtml = '', extraSettingsHtml = '') {
  return `
    <div class="print-plan-sidebar-card">
      <div class="print-plan-sidebar-card-title">${escapeHtml(t('modal.drucken.label.ansichten', 'Ansichten'))}</div>
      <div class="print-plan-sidebar-card-options">
        <button type="button" class="btn print-plan-sidebar-option" data-print-plan-action="add-3d-view">3D</button>
        ${extraButtonsHtml}
      </div>
      ${selectedWindowSettingsHtml()}
      ${extraSettingsHtml}
    </div>
  `;
}

// Shown in place of/alongside the "3D" button whenever a window is selected
// (Dreh-Modus) — per-window Ebenen (preset + Boden/Doppelboden/Decke/
// Doppeldecke visibility) and Beleuchtung (Helligkeit +, for Aussenbereich,
// Sonnenstand). Independent per window — never touches the project's own
// global Ebenen/Beleuchtung settings, see applyAmbientSceneState() below.
function selectedWindowSettingsHtml() {
  const win = findWindow(selectedWindowId);
  if (!win || win.type !== '3d' || !win.sceneFilters) return '';
  const sf = win.sceneFilters;
  const presetButtons = WINDOW_PRESET_IDS.map(id => `<button type="button" class="btn print-plan-sidebar-option${sf.preset === id ? ' is-active' : ''}" data-print-plan-window-preset="${id}">${escapeHtml(presetLabel(id))}</button>`).join('');
  const filterButtons = WINDOW_FILTER_KEYS.map(key => `<button type="button" class="btn print-plan-sidebar-option${sf.filters[key] ? ' is-active' : ''}" data-print-plan-window-filter="${key}">${escapeHtml(filterLabel(key))}</button>`).join('');
  const sunTimeHtml = sf.preset === 'outdoor'
    ? `<div class="print-plan-sidebar-card-options print-plan-sidebar-card-options--row print-plan-sidebar-window-suntime">${OUTDOOR_SUN_TIMES.map(time => `<button type="button" class="btn print-plan-sidebar-option${sf.sunTime === time ? ' is-active' : ''}" data-print-plan-window-suntime="${time}">${escapeHtml(time)}</button>`).join('')}</div>`
    : '';
  return `
    <div class="print-plan-sidebar-window-settings">
      <div class="print-plan-sidebar-subtitle">${escapeHtml(t('modal.drucken.label.ebenen', 'Ebenen'))}</div>
      <div class="print-plan-sidebar-card-options print-plan-sidebar-card-options--grid2">${presetButtons}</div>
      <div class="print-plan-sidebar-card-options print-plan-sidebar-card-options--grid2 print-plan-sidebar-section--divided">${filterButtons}</div>
      <div class="print-plan-sidebar-subtitle print-plan-sidebar-section--divided">${escapeHtml(t('modal.drucken.label.beleuchtung', 'Beleuchtung'))}</div>
      <label class="print-plan-sidebar-window-brightness">
        <span>${escapeHtml(t('modal.drucken.wert.helligkeit', 'Helligkeit'))}</span>
        <input type="range" min="40" max="180" step="10" value="${sf.brightness}" data-print-plan-window-brightness>
        <output>${sf.brightness}%</output>
      </label>
      ${sunTimeHtml}
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Geometry sync (mm -> on-screen px, driven by the print modal's own fitScale)
// ---------------------------------------------------------------------------

function applyWindowGeometry(win, el) {
  el.style.left = `${win.xMm * lastFitScale}px`;
  el.style.top = `${win.zMm * lastFitScale}px`;
  el.style.width = `${win.widthMm * lastFitScale}px`;
  el.style.height = `${win.heightMm * lastFitScale}px`;
}

function syncViewWindowsGeometry(fitScale, frameMm, titleBlockGeometry) {
  lastFitScale = fitScale;
  lastFrameMm = frameMm;
  lastTitleBlockGeometry = titleBlockGeometry;
  if (!layerEl) return;
  windowsState.forEach(win => {
    const el = layerEl.querySelector(`[data-view-window-id="${win.id}"]`);
    if (el) applyWindowGeometry(win, el);
  });
  resizeLiveInstanceToHost();
}

// ---------------------------------------------------------------------------
// Snap-Referenzen + Hinweislinien (releasable, nichts wird persistiert — nur
// während der laufenden Drag-Geste in startMove/startResize relevant)
// ---------------------------------------------------------------------------

function currentReferenceLines(excludeId) {
  return collectReferenceLines(lastFrameMm, lastTitleBlockGeometry, windowsState.filter(win => win.id !== excludeId));
}

function setSnapGuide(axis, lineMm) {
  if (!layerEl) return;
  const cached = axis === 'x' ? snapGuideVerticalEl : snapGuideHorizontalEl;
  if (lineMm == null) {
    if (cached) cached.hidden = true;
    return;
  }
  let el = cached;
  if (!el) {
    el = document.createElement('div');
    el.className = `print-plan-window-snap-guide print-plan-window-snap-guide--${axis === 'x' ? 'vertical' : 'horizontal'}`;
    layerEl.appendChild(el);
    if (axis === 'x') snapGuideVerticalEl = el; else snapGuideHorizontalEl = el;
  }
  el.style[axis === 'x' ? 'left' : 'top'] = `${lineMm * lastFitScale}px`;
  el.hidden = false;
}

// ---------------------------------------------------------------------------
// Window chrome (DOM + pointer-event state machine)
// ---------------------------------------------------------------------------

function isSelected(id) {
  return selectedWindowId === id;
}

function findWindow(id) {
  return windowsState.find(win => win.id === id) || null;
}

function renderWindowContent(win, el) {
  const host = el.querySelector('.print-plan-3d-window-canvas-host');
  if (!host) return;
  host.innerHTML = win.snapshot ? `<img alt="" draggable="false" src="${escapeHtml(win.snapshot)}">` : '';
}

function buildWindowElement(win) {
  const el = document.createElement('div');
  el.className = 'print-plan-3d-window';
  el.dataset.viewWindowId = win.id;
  el.innerHTML = `
    <div class="print-plan-3d-window-canvas-host"></div>
    <button type="button" class="print-plan-3d-window-delete" title="${escapeHtml(t('modal.drucken.wert.fenster_entfernen', 'Fenster entfernen'))}" aria-label="${escapeHtml(t('modal.drucken.wert.fenster_entfernen', 'Fenster entfernen'))}">&times;</button>
    <div class="print-plan-3d-window-handle nw" data-handle="nw"></div>
    <div class="print-plan-3d-window-handle ne" data-handle="ne"></div>
    <div class="print-plan-3d-window-handle sw" data-handle="sw"></div>
    <div class="print-plan-3d-window-handle se" data-handle="se"></div>
  `;
  renderWindowContent(win, el);
  applyWindowGeometry(win, el);

  el.addEventListener('pointerdown', event => handleWindowPointerDown(win, el, event));
  el.addEventListener('wheel', event => {
    if (isSelected(win.id)) event.stopPropagation();
  }, { passive: true });
  const deleteBtn = el.querySelector('.print-plan-3d-window-delete');
  // Needs its own pointerdown stop, not just click's — otherwise the
  // window's own pointerdown handler (bubbling up from this button) already
  // starts a move/select interaction before the click ever reaches here.
  deleteBtn.addEventListener('pointerdown', event => event.stopPropagation());
  deleteBtn.addEventListener('click', event => {
    event.stopPropagation();
    removeViewWindow(win.id);
  });

  return el;
}

function handleWindowPointerDown(win, el, event) {
  if (event.button !== undefined && event.button !== 0) return;
  // Always stop this from bubbling to #printPlanViewer's own pan/zoom
  // handlers — without this, dragging/resizing/orbiting a window would
  // simultaneously pan the 2D print preview underneath it.
  event.stopPropagation();

  if (isSelected(win.id)) {
    // Orbit mode: the live canvas has its own OrbitControls pointer
    // listeners bound directly to it (fired already, before this bubbles
    // here) — nothing else to do, just don't move/resize.
    return;
  }

  const handle = event.target.closest('.print-plan-3d-window-handle')?.dataset.handle;
  if (handle) {
    startResize(win, el, handle, event);
    return;
  }
  startMove(win, el, event);
}

function startMove(win, el, event) {
  const pointerId = event.pointerId;
  const startScreen = { x: event.clientX, y: event.clientY };
  const startMm = { x: win.xMm, z: win.zMm };
  const moveSnapState = { x: null, z: null };
  let moved = false;
  el.setPointerCapture(pointerId);

  const onMove = moveEvent => {
    if (moveEvent.pointerId !== pointerId) return;
    const dxPx = moveEvent.clientX - startScreen.x;
    const dyPx = moveEvent.clientY - startScreen.y;
    if (Math.hypot(dxPx, dyPx) > 2) moved = true;
    const rawDxMm = dxPx / lastFitScale;
    const rawDzMm = dyPx / lastFitScale;
    const leftRaw = startMm.x + rawDxMm, rightRaw = leftRaw + win.widthMm;
    const topRaw = startMm.z + rawDzMm, bottomRaw = topRaw + win.heightMm;
    const { vertical, horizontal } = currentReferenceLines(win.id);

    moveSnapState.x = updateAxisSnap(moveSnapState.x, [{ offset: 0, value: leftRaw }, { offset: 1, value: rightRaw }], vertical, lastFrameMm.borderMm);
    moveSnapState.z = updateAxisSnap(moveSnapState.z, [{ offset: 0, value: topRaw }, { offset: 1, value: bottomRaw }], horizontal, lastFrameMm.borderMm);

    const snappedDxMm = moveSnapState.x ? rawDxMm + (moveSnapState.x.line - moveSnapState.x.edgeValue) : rawDxMm;
    const snappedDzMm = moveSnapState.z ? rawDzMm + (moveSnapState.z.line - moveSnapState.z.edgeValue) : rawDzMm;
    win.xMm = startMm.x + snappedDxMm;
    win.zMm = startMm.z + snappedDzMm;
    applyWindowGeometry(win, el);
    setSnapGuide('x', moveSnapState.x ? moveSnapState.x.guideLine : null);
    setSnapGuide('z', moveSnapState.z ? moveSnapState.z.guideLine : null);
  };
  const onUp = upEvent => {
    if (upEvent.pointerId !== pointerId) return;
    el.releasePointerCapture(pointerId);
    el.removeEventListener('pointermove', onMove);
    el.removeEventListener('pointerup', onUp);
    el.removeEventListener('pointercancel', onUp);
    setSnapGuide('x', null);
    setSnapGuide('z', null);
    if (!moved) selectViewWindow(win.id, el);
    else onGeometryCommit?.();
  };
  el.addEventListener('pointermove', onMove);
  el.addEventListener('pointerup', onUp);
  el.addEventListener('pointercancel', onUp);
}

function startResize(win, el, handle, event) {
  const pointerId = event.pointerId;
  const startScreen = { x: event.clientX, y: event.clientY };
  const start = { xMm: win.xMm, zMm: win.zMm, widthMm: win.widthMm, heightMm: win.heightMm };
  const resizeSnapState = { x: null, z: null };
  el.setPointerCapture(pointerId);

  const onMove = moveEvent => {
    if (moveEvent.pointerId !== pointerId) return;
    let dxMm = (moveEvent.clientX - startScreen.x) / lastFitScale;
    let dzMm = (moveEvent.clientY - startScreen.y) / lastFitScale;
    const { vertical, horizontal } = currentReferenceLines(win.id);

    // Snap-Korrektur läuft VOR der bestehenden Clamp-Logik unten und
    // korrigiert dxMm/dzMm, statt sie zu ersetzen — die MIN_WINDOW_MM-Clamps
    // bleiben dadurch unverändert wirksam. wouldFloor verwirft den Snap für
    // diesen Tick komplett (nicht nur die Hinweislinie), falls er das
    // Fenster über die reine Cursor-Position hinaus verkleinern würde.
    if (handle.includes('e') || handle.includes('w')) {
      const trailing = handle.includes('e');
      const rawEdge = trailing ? start.xMm + start.widthMm + dxMm : start.xMm + dxMm;
      const next = updateAxisSnap(resizeSnapState.x, [{ offset: trailing ? 1 : 0, value: rawEdge }], vertical, lastFrameMm.borderMm);
      if (next) {
        const correctedDx = dxMm + (next.line - rawEdge);
        const wouldFloor = trailing ? (start.widthMm + correctedDx) < MIN_WINDOW_MM : (start.widthMm - correctedDx) < MIN_WINDOW_MM;
        resizeSnapState.x = wouldFloor ? null : next;
        if (!wouldFloor) dxMm = correctedDx;
      } else resizeSnapState.x = null;
    } else resizeSnapState.x = null;

    if (handle.includes('s') || handle.includes('n')) {
      const trailing = handle.includes('s');
      const rawEdge = trailing ? start.zMm + start.heightMm + dzMm : start.zMm + dzMm;
      const next = updateAxisSnap(resizeSnapState.z, [{ offset: trailing ? 1 : 0, value: rawEdge }], horizontal, lastFrameMm.borderMm);
      if (next) {
        const correctedDz = dzMm + (next.line - rawEdge);
        const wouldFloor = trailing ? (start.heightMm + correctedDz) < MIN_WINDOW_MM : (start.heightMm - correctedDz) < MIN_WINDOW_MM;
        resizeSnapState.z = wouldFloor ? null : next;
        if (!wouldFloor) dzMm = correctedDz;
      } else resizeSnapState.z = null;
    } else resizeSnapState.z = null;

    if (handle.includes('e')) win.widthMm = Math.max(MIN_WINDOW_MM, start.widthMm + dxMm);
    if (handle.includes('w')) {
      const newWidth = Math.max(MIN_WINDOW_MM, start.widthMm - dxMm);
      win.xMm = start.xMm + (start.widthMm - newWidth);
      win.widthMm = newWidth;
    }
    if (handle.includes('s')) win.heightMm = Math.max(MIN_WINDOW_MM, start.heightMm + dzMm);
    if (handle.includes('n')) {
      const newHeight = Math.max(MIN_WINDOW_MM, start.heightMm - dzMm);
      win.zMm = start.zMm + (start.heightMm - newHeight);
      win.heightMm = newHeight;
    }
    applyWindowGeometry(win, el);
    resizeLiveInstanceToHost();
    setSnapGuide('x', resizeSnapState.x ? resizeSnapState.x.guideLine : null);
    setSnapGuide('z', resizeSnapState.z ? resizeSnapState.z.guideLine : null);
  };
  const onUp = upEvent => {
    if (upEvent.pointerId !== pointerId) return;
    el.releasePointerCapture(pointerId);
    el.removeEventListener('pointermove', onMove);
    el.removeEventListener('pointerup', onUp);
    el.removeEventListener('pointercancel', onUp);
    setSnapGuide('x', null);
    setSnapGuide('z', null);
    onGeometryCommit?.();
  };
  el.addEventListener('pointermove', onMove);
  el.addEventListener('pointerup', onUp);
  el.addEventListener('pointercancel', onUp);
}

function removeViewWindow(id) {
  if (isSelected(id)) deselectViewWindow();
  windowsState = windowsState.filter(win => win.id !== id);
  layerEl?.querySelector(`[data-view-window-id="${id}"]`)?.remove();
  onGeometryCommit?.();
}

// Serializes ALL async work that mutates the shared S.view3d scene state
// (snapshot rendering, live-mount ambient-state apply, deselect/print
// restores, per-window Ebenen/Beleuchtung setters) so none of it can overlap.
// Originally just for snapshot generation (to avoid spinning up multiple
// concurrent WebGLRenderers — same WebGL-context-exhaustion concern as
// viewWindowsPrintHtml() below), now generalized: per-window scene-filter
// switching added more call sites that touch S.view3d.modalScenePreset/
// .modalFilters and call build3DScene(), and two similar races were already
// found+fixed this session (WebGL context exhaustion, camera-pose
// cross-contamination) from exactly this kind of unserialized overlap.
// Exportiert (siehe Modul-Export-Liste unten), damit print_plan_2d_views.js
// SEINE Bakes über DIESELBE Queue laufen lässt statt eine eigene, unkoordinierte
// zu führen — beide Module bauen WebGLRenderer und mutieren denselben
// S.view3d.modalFilters/scene-Zustand über build3DScene(); zwei unabhängige
// Queues hätten dieselbe Kontext-Erschöpfungs-/Race-Gefahr NUR INNERHALB jedes
// Moduls vermieden, nicht ZWISCHEN beiden (ein 2D- und ein 3D-Bake konnten
// bisher gleichzeitig laufen und sich gegenseitig die modalFilters
// überschreiben).
let sceneWorkQueue = Promise.resolve();
function queueSceneWork(fn) {
  const result = sceneWorkQueue.then(fn);
  // Keeps the queue itself always resolved, even if this step rejected —
  // otherwise one failed step would permanently wedge every later queued
  // step behind a rejected promise. The caller's own `result` still rejects
  // normally for its own error handling.
  sceneWorkQueue = result.then(() => undefined, () => undefined);
  return result;
}

function addViewWindow() {
  const win = createViewWindow();
  windowsState.push(win);
  const el = buildWindowElement(win);
  layerEl?.appendChild(el);
  onGeometryCommit?.();
  // A brand-new window has no snapshot yet, so it would otherwise show an
  // empty grey box (just the bare .print-plan-3d-window background) until
  // the user manually selects and deselects it once. Render one immediately
  // in the background, without entering orbit/select mode.
  queueSceneWork(() => generateInitialSnapshot(win, el))
    .catch(error => console.error('Snapshot-Warteschlange für 3D-Fenster fehlgeschlagen', error));
  return win;
}

async function generateInitialSnapshot(win, el) {
  if (!windowsState.includes(win)) return; // removed again before its turn came up
  const lifecycleGeneration = viewWindowLifecycleGeneration;
  const selection = currentPrintGroupSelection();
  try {
    const dataUrl = await renderPlaceholderSnapshot(win, selection);
    if (!windowsState.includes(win)
      || lifecycleGeneration !== viewWindowLifecycleGeneration
      || selection.signature !== selectedPrintGroupSignature) return; // removed/changed while rendering
    win.snapshot = dataUrl;
    win.snapshotUpdatedAt = Date.now();
    win.snapshotGroupSelectionSignature = selection.signature;
    if (!isSelected(win.id)) renderWindowContent(win, el);
    onGeometryCommit?.();
  } catch (error) {
    console.error('Snapshot für neues 3D-Fenster konnte nicht erzeugt werden', error);
  }
}

// ---------------------------------------------------------------------------
// Live 3D instance (exactly one at a time — see module header)
// ---------------------------------------------------------------------------

function sphericalOffset(theta, phi, radius) {
  return {
    x: radius * Math.sin(phi) * Math.sin(theta),
    y: radius * Math.cos(phi),
    z: radius * Math.sin(phi) * Math.cos(theta)
  };
}

function objectEffectivelyVisible(object, root) {
  let current = object;
  while (current && current !== root?.parent) {
    if (current.visible === false) return false;
    if (current === root) break;
    current = current.parent;
  }
  return true;
}

function objectExcludedFromPrintFraming(object, root) {
  let current = object;
  while (current && current !== root?.parent) {
    if (current.userData?.keep3DBase
      || current.userData?.modalLevelFilter
      || current.userData?.modalBackgroundFilter) return true;
    if (current === root) break;
    current = current.parent;
  }
  return false;
}

function visibleProjectBounds3D(THREE) {
  const root = S.view3d?.projectRoot3D;
  const fallback = S.view3d?.bounds;
  if (!THREE?.Box3 || !root?.traverse) return fallback;
  root.updateWorldMatrix?.(true, true);
  const bounds = new THREE.Box3();
  const objectBounds = new THREE.Box3();
  let found = false;
  root.traverse(object => {
    if (!(object?.isMesh || object?.isLine || object?.isLineSegments)) return;
    if (!objectEffectivelyVisible(object, root)) return;
    if (objectExcludedFromPrintFraming(object, root)) return;
    objectBounds.setFromObject(object);
    if (objectBounds.isEmpty()) return;
    bounds.union(objectBounds);
    found = true;
  });
  if (!found || bounds.isEmpty()) return fallback;
  return {
    minX: bounds.min.x,
    maxX: bounds.max.x,
    minY: bounds.min.y,
    maxY: bounds.max.y,
    minZ: bounds.min.z,
    maxZ: bounds.max.z
  };
}

function defaultCameraPose(scene3d, THREE, selection = currentPrintGroupSelection()) {
  const visibleBounds = visibleProjectBounds3D(THREE);
  const selectedBounds = selectedPrintPlanGroupBounds({
    selectedGroupKeys: selection.selectedGroupKeys
  });
  const bounds = selectedBounds
    ? {
        ...(visibleBounds || {}),
        minX: selectedBounds.minX,
        maxX: selectedBounds.maxX,
        minZ: selectedBounds.minZ,
        maxZ: selectedBounds.maxZ
      }
    : visibleBounds;
  const targetY = bounds ? scene3d.calculate3DTargetHeight(bounds) : 900;
  const radius = bounds ? scene3d.calculate3DCameraRadius(bounds) : 6000;
  const target = {
    x: bounds ? (bounds.minX + bounds.maxX) / 2 : 0,
    y: targetY,
    z: bounds ? (bounds.minZ + bounds.maxZ) / 2 : 0
  };
  const offset = sphericalOffset(DEFAULT_THETA, DEFAULT_PHI, radius);
  return {
    target,
    position: { x: target.x + offset.x, y: target.y + offset.y, z: target.z + offset.z }
  };
}

// ---------------------------------------------------------------------------
// Per-window Ebenen/Beleuchtung — apply/restore against the shared scene
// ---------------------------------------------------------------------------
//
// The app's global Ebenen/Beleuchtung system (scene3d.js) is a singleton on
// the one shared S.view3d.scene: visibility is a property of the objects
// themselves (not per-camera), and a preset switch needs a full geometry
// rebuild (different floor heights/materials per preset). Since only one
// print window is ever "live" at a time (module header), and the print bake
// is already sequential, we follow the exact pattern already established for
// scene.background: apply this window's own state to the shared scene right
// before rendering it, then restore whatever was active before — never
// touching S.project.levels/S.project.outdoorEnvironment (the persistent,
// user-configured project settings) in the process.

function captureAmbientSceneFilters(modules) {
  const preset = modules.scene3d.current3DModalScenePreset(S.view3d);
  const filters = { ...modules.scene3d.normalize3DModalFilters(S.view3d) };
  const env = normalizeOutdoorEnvironment(S.project?.outdoorEnvironment, S.project?.floorplan, S.project?.outdoorSunSimulation);
  return { preset, filters, brightness: env.sunBrightness, sunTime: env.sunTime };
}

// Defaults a window's sceneFilters, once, to whatever is currently active —
// "Standardwerte = Stand zum Erstellungszeitpunkt". Lazy rather than done at
// createViewWindow() time so window creation itself stays synchronous; since
// the print modal fully covers the app while open, the ambient state can't
// actually change before this first resolves.
function resolveWindowSceneFilters(win, modules) {
  if (!win.sceneFilters) win.sceneFilters = captureAmbientSceneFilters(modules);
  return win.sceneFilters;
}

// Sets the transient S.view3d scratch fields (never S.project.levels) and
// calls build3DScene(). The explicit apply3DModalSceneFilters() call after it
// is NOT redundant: build3DScene()'s own signature check (build3DSceneSignatureDetails/
// buildRelevant3DModalSignature) only covers modalScenePreset and
// filters.ceiling — a floor/doubleFloor/doubleCeiling-only change leaves that
// signature untouched, so without this explicit call here those 3 toggles
// would have no visible effect until some OTHER change happened to force a
// real rebuild (verified empirically — confirmed by Playwright: toggling
// floor/doubleFloor did nothing until a subsequent ceiling/preset change
// dragged both changes in at once).
async function applyAmbientSceneState(sceneFilters, modules) {
  S.view3d.modalScenePreset = sceneFilters.preset;
  S.view3d.modalFilters = { ...sceneFilters.filters };
  await modules.scene3d.build3DScene({ preserveCamera: true });
  modules.scene3d.apply3DModalSceneFilters();
}

// Repositions/re-intensities the 3 shared THREE.Light objects (not cloned per
// window) for this window's own brightness (+ sun position, when preset is
// 'outdoor') — cheap, no scene rebuild. `renderer` may be null (used for the
// ambient-restore path, where no window renderer is involved).
//
// Uses update3DLightingForBounds() rather than the narrower
// updateOutdoorLightIntensities3D() because sun POSITION (not just
// intensity) needs updating too — that live in update3DLightingForBounds()
// only. That function has two hidden side effects: it hardcodes reading
// S.project.outdoorEnvironment (the persistent project setting, for
// brightness/sunTime) AND hardcodes writing S.view3d?.renderer's tone-mapping
// exposure + shadow-map-enabled flag (the real 3D-modal's own renderer, kept
// alive for the whole session once created). Both are saved/restored locally
// around the call so this window's own settings feed in without ever
// touching the persistent project setting or the main renderer's state.
function applySceneLighting(sceneFilters, modules, renderer) {
  const bounds = S.view3d?.bounds;
  if (bounds) {
    const previousOutdoorEnvironment = S.project.outdoorEnvironment;
    const mainRenderer = S.view3d?.renderer;
    const previousMainExposure = mainRenderer?.toneMappingExposure;
    const previousMainShadowMapEnabled = mainRenderer?.shadowMap?.enabled;

    S.project.outdoorEnvironment = {
      ...normalizeOutdoorEnvironment(previousOutdoorEnvironment, S.project?.floorplan, S.project?.outdoorSunSimulation),
      enabled: sceneFilters.preset === 'outdoor',
      sunBrightness: sceneFilters.brightness,
      sunTime: sceneFilters.sunTime
    };
    modules.scene3d.update3DLightingForBounds(bounds);
    S.project.outdoorEnvironment = previousOutdoorEnvironment;

    if (mainRenderer) {
      if (previousMainExposure !== undefined) mainRenderer.toneMappingExposure = previousMainExposure;
      if (mainRenderer.shadowMap && previousMainShadowMapEnabled !== undefined) mainRenderer.shadowMap.enabled = previousMainShadowMapEnabled;
    }
  }

  const outdoorEnv = sceneFilters.preset === 'outdoor'
    ? normalizeOutdoorEnvironment({ sunBrightness: sceneFilters.brightness, sunTime: sceneFilters.sunTime }, S.project?.floorplan, null)
    : null;
  modules.scene3d.applySceneToneMappingExposure3D(renderer, outdoorEnv);
}

async function applyWindowSceneFilters(win, modules, renderer) {
  const sceneFilters = resolveWindowSceneFilters(win, modules);
  await applyAmbientSceneState(sceneFilters, modules);
  applySceneLighting(sceneFilters, modules, renderer);
  return sceneFilters;
}

// Fire-and-forget restore, queued so it can never overlap with another
// scene-mutating operation. Used whenever a window's live/applied state needs
// to be undone: normal deselect, or a mount that got cancelled mid-flight.
function scheduleAmbientSceneRestore(sceneFilters, modules) {
  if (!sceneFilters || !modules) return;
  queueSceneWork(async () => {
    await applyAmbientSceneState(sceneFilters, modules);
    applySceneLighting(sceneFilters, modules, null);
  }).catch(error => console.error('Ebenen-/Beleuchtungszustand konnte nicht zurückgesetzt werden', error));
}

function resizeLiveInstanceToHost() {
  if (!liveInstance) return;
  const rect = liveInstance.host.getBoundingClientRect();
  const width = Math.max(1, Math.round(rect.width));
  const height = Math.max(1, Math.round(rect.height));
  liveInstance.renderer.setSize(width, height, false);
  const aspect = width / height;
  if (liveInstance.camera.isOrthographicCamera) {
    const viewHeight = Math.abs(liveInstance.camera.top - liveInstance.camera.bottom) || 1000;
    liveInstance.camera.left = -viewHeight * aspect / 2;
    liveInstance.camera.right = viewHeight * aspect / 2;
  } else {
    liveInstance.camera.aspect = aspect;
  }
  liveInstance.camera.updateProjectionMatrix();
  liveInstance.pseudoView3d.renderDirty = true;
  scheduleLiveFrame();
}

function scheduleLiveFrame() {
  if (!liveInstance || liveInstance.frameHandle || liveInstance.disposed) return;
  liveInstance.frameHandle = requestAnimationFrame(() => {
    if (!liveInstance || liveInstance.disposed) return;
    liveInstance.frameHandle = 0;
    const controlsChanged = liveInstance.modules.threeControls.update(liveInstance.pseudoView3d) === true;
    if (controlsChanged) liveInstance.pseudoView3d.renderDirty = true;
    if (liveInstance.pseudoView3d.renderDirty) {
      liveInstance.renderer.render(liveInstance.scene, liveInstance.camera);
      liveInstance.modules.viewcube.update(liveInstance.pseudoView3d);
      liveInstance.pseudoView3d.renderDirty = false;
    }
    if (controlsChanged) scheduleLiveFrame();
  });
}

// Takes an explicit instance reference (not just the module-level
// `liveInstance`) so a delayed teardown from an older select/deselect cycle
// can never dispose whatever NEWER instance has since been mounted — see the
// comment on freezeLiveInstanceToSnapshot() for why that race matters.
function teardownLiveInstance(instance = liveInstance) {
  if (!instance || instance.disposed) return;
  instance.disposed = true;
  if (instance.frameHandle) cancelAnimationFrame(instance.frameHandle);
  instance.modules.viewcube.dispose(instance.pseudoView3d);
  instance.orbitControls?.dispose?.();
  releaseWebGLRenderer(instance.renderer);
  instance.viewCubeHost?.remove();
  instance.projectionToggle?.remove();
  instance.printGroupFilter?.restore?.();
  instance.scene.background = instance.previousSceneBackground;
  if (liveInstance === instance) liveInstance = null;
}

// Captures the live instance into a local at the very start, before the
// first `await` — everything from here on operates on that captured
// reference rather than re-reading the module-level `liveInstance`. Without
// this, selecting a second window while this snapshot capture is still
// in-flight would overwrite the module-level `liveInstance` with the NEW
// window's instance before this function's post-await lines run, corrupting
// `win.camera.*` with the wrong window's pose and (via the caller's
// subsequent teardownLiveInstance() call) tearing down the new window's
// freshly-mounted renderer instead of this one's.
async function freezeLiveInstanceToSnapshot(win) {
  const instance = liveInstance;
  if (!instance) return;
  instance.renderer.render(instance.scene, instance.camera);
  const source = instance.renderer.domElement;
  const small = document.createElement('canvas');
  const scale = Math.min(1, PLACEHOLDER_SNAPSHOT_MAX_EDGE_PX / Math.max(source.width, source.height));
  small.width = Math.max(1, Math.round(source.width * scale));
  small.height = Math.max(1, Math.round(source.height * scale));
  const ctx = small.getContext('2d');
  ctx.drawImage(source, 0, 0, small.width, small.height);

  // Written synchronously, BEFORE the await below — a fast deselect+reselect
  // of the SAME window (a later click, handled only after this whole
  // synchronous chain unwinds) must always see the just-oriented pose, never
  // a stale/default one because the PNG encoding below was still pending.
  win.camera.position = { ...instance.camera.position };
  win.camera.target = { ...instance.pseudoView3d.target };
  win.camera.projectionMode = instance.pseudoView3d.projectionMode;
  win.camera.orthographicViewHeight = instance.camera.isOrthographicCamera
    ? Math.abs(instance.camera.top - instance.camera.bottom)
    : win.camera.orthographicViewHeight;

  try {
    win.snapshot = await instance.modules.snapshotController.captureCanvasPngDataUrl(small);
    win.snapshotUpdatedAt = Date.now();
    win.snapshotGroupSelectionSignature = instance.printGroupSelectionSignature || selectedPrintGroupSignature;
  } catch (_error) {
    // The caller invalidates the old bitmap before capture. Keeping it would
    // associate an outdated image with the newly persisted camera pose.
  }
}

function setLiveProjectionMode(mode) {
  if (!liveInstance) return;
  const nextMode = mode === 'orthographic' ? 'orthographic' : 'perspective';
  if (nextMode === liveInstance.pseudoView3d.projectionMode) return;
  const THREE = liveInstance.THREE;
  const rect = liveInstance.host.getBoundingClientRect();
  const aspect = Math.max(1, rect.width) / Math.max(1, rect.height);
  const target = liveInstance.pseudoView3d.target;
  const camera = liveInstance.camera;
  const targetVec = new THREE.Vector3(target.x, target.y, target.z);
  const distance = Math.max(1, camera.position.distanceTo(targetVec));
  let viewHeight;
  if (nextMode === 'orthographic') {
    const fov = camera.isPerspectiveCamera ? camera.fov : 45;
    viewHeight = 2 * distance * Math.tan(THREE.MathUtils.degToRad(fov) / 2);
  } else {
    viewHeight = Math.abs(camera.top - camera.bottom) || 1000;
  }
  const nextCamera = liveInstance.modules.scene3d.create3DCamera(THREE, nextMode, aspect, { viewHeight });
  nextCamera.position.copy(camera.position);
  nextCamera.up.set(0, 1, 0);
  nextCamera.lookAt(targetVec);
  liveInstance.camera = nextCamera;
  liveInstance.pseudoView3d.camera = nextCamera;
  liveInstance.pseudoView3d.projectionMode = nextMode;
  if (liveInstance.orbitControls) liveInstance.orbitControls.object = nextCamera;
  liveInstance.projectionToggle.querySelectorAll('[data-window-projection]').forEach(btn => {
    btn.classList.toggle('is-active', btn.dataset.windowProjection === nextMode);
  });
  liveInstance.pseudoView3d.renderDirty = true;
  scheduleLiveFrame();
}

async function mountLiveInstance(win, el) {
  const host = el.querySelector('.print-plan-3d-window-canvas-host');
  if (!host) return;
  host.innerHTML = `<div class="print-plan-3d-window-loading">${escapeHtml(t('modal.drucken.wert.3d_wird_geladen', '3D wird geladen...'))}</div>`;

  const lifecycleGeneration = viewWindowLifecycleGeneration;
  const selection = currentPrintGroupSelection();
  const { modules, THREE, previousSceneFilters, sceneFilters, printGroupFilter } = await queueSceneWork(async () => {
    const loadedModules = await load3DModules();
    const threeRuntime = await loadedModules.scene3d.ensure3D();
    const ambientBaseline = captureAmbientSceneFilters(loadedModules);
    const appliedSceneFilters = await applyWindowSceneFilters(win, loadedModules, null);
    const appliedPrintGroupFilter = applyPrintPlanSceneGroupFilter(S.view3d?.projectRoot3D, {
      selectedGroupKeys: selection.selectedGroupKeys
    });
    return {
      modules: loadedModules,
      THREE: threeRuntime,
      previousSceneFilters: ambientBaseline,
      sceneFilters: appliedSceneFilters,
      printGroupFilter: appliedPrintGroupFilter
    };
  });
  if (!isSelected(win.id)
    || lifecycleGeneration !== viewWindowLifecycleGeneration
    || selection.signature !== selectedPrintGroupSignature) {
    // Deselected again before the mount finished — the queued step above
    // already applied this window's preset/filters/lighting to the shared
    // scene; undo that now, since there will be no live instance around to
    // eventually restore it otherwise.
    printGroupFilter.restore();
    scheduleAmbientSceneRestore(previousSceneFilters, modules);
    return;
  }
  onSidebarRefreshNeeded?.();

  const scene = S.view3d.scene;
  const rect = host.getBoundingClientRect();
  const width = Math.max(1, Math.round(rect.width));
  const height = Math.max(1, Math.round(rect.height));
  const aspect = width / height;
  const defaultPose = defaultCameraPose(modules.scene3d, THREE, selection);
  const pose = (win.camera.position && win.camera.target) ? win.camera : { ...defaultPose, projectionMode: win.camera.projectionMode, orthographicViewHeight: win.camera.orthographicViewHeight };

  const camera = modules.scene3d.create3DCamera(THREE, win.camera.projectionMode, aspect, { viewHeight: pose.orthographicViewHeight || undefined });
  const posVec = pose.position || defaultPose.position;
  const targetVec = pose.target || defaultPose.target;
  camera.position.set(posVec.x, posVec.y, posVec.z);
  camera.up.set(0, 1, 0);
  camera.lookAt(targetVec.x, targetVec.y, targetVec.z);

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setClearColor(0x000000, 0);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setSize(width, height, false);
  host.innerHTML = '';
  host.appendChild(renderer.domElement);
  // Re-applied now that the window's own renderer exists — the earlier call
  // inside the queued step above passed renderer=null (tone-mapping exposure
  // guards itself against that), so light intensities were already correct
  // but exposure wasn't yet targeted at this specific renderer.
  applySceneLighting(sceneFilters, modules, renderer);

  // scene.background belongs to the SHARED S.view3d.scene (also used by the
  // real 3D modal), so this window must not want it — no background color at
  // all in the print output. Blanked for the lifetime of this live instance,
  // restored in teardownLiveInstance() so the main 3D view is unaffected.
  const previousSceneBackground = scene.background;
  scene.background = null;

  const viewCubeHost = document.createElement('div');
  viewCubeHost.className = 'print-plan-3d-window-viewcube-host';
  viewCubeHost.setAttribute('aria-hidden', 'true');
  el.appendChild(viewCubeHost);

  const projectionToggle = document.createElement('div');
  projectionToggle.className = 'print-plan-3d-window-projection-toggle';
  projectionToggle.innerHTML = `
    <button type="button" data-window-projection="perspective" class="${win.camera.projectionMode === 'perspective' ? 'is-active' : ''}">P</button>
    <button type="button" data-window-projection="orthographic" class="${win.camera.projectionMode === 'orthographic' ? 'is-active' : ''}">O</button>
  `;
  projectionToggle.addEventListener('pointerdown', event => event.stopPropagation());
  projectionToggle.addEventListener('click', event => {
    event.stopPropagation();
    const btn = event.target.closest('[data-window-projection]');
    if (btn) setLiveProjectionMode(btn.dataset.windowProjection);
  });
  el.appendChild(projectionToggle);

  // three_controls.js's initialize() re-derives camera.position from
  // pseudoView3d.controls.theta/phi/radius (legacyCameraOffset()) rather than
  // trusting the position we just set above — so theta/phi MUST be computed
  // from the actual restored pose, not left at the module's spherical
  // defaults, or every mount would silently snap back to the default view
  // (only the radius/distance would survive). Same formula as
  // three_controls.js's own syncLegacyState().
  const orbitOffset = new THREE.Vector3(posVec.x - targetVec.x, posVec.y - targetVec.y, posVec.z - targetVec.z);
  const orbitSpherical = new THREE.Spherical().setFromVector3(orbitOffset);

  const pseudoView3d = {
    camera,
    scene,
    target: { x: targetVec.x, y: targetVec.y, z: targetVec.z },
    controls: { theta: Math.atan2(orbitOffset.x, orbitOffset.z), phi: orbitSpherical.phi, radius: orbitSpherical.radius },
    projectionMode: win.camera.projectionMode,
    cameraInitialized: true,
    renderDirty: true,
    requestFrame: () => scheduleLiveFrame()
  };

  const orbitControls = modules.threeControls.initialize(pseudoView3d, renderer.domElement);
  modules.viewcube.initialize(pseudoView3d, { host: viewCubeHost });

  liveInstance = {
    id: win.id,
    el,
    host,
    viewCubeHost,
    projectionToggle,
    THREE,
    modules,
    renderer,
    scene,
    previousSceneBackground,
    previousSceneFilters,
    printGroupFilter,
    printGroupSelectionSignature: selection.signature,
    camera,
    pseudoView3d,
    orbitControls,
    frameHandle: 0,
    disposed: false
  };
  scheduleLiveFrame();
}

function selectViewWindow(id, el) {
  if (isSelected(id)) return;
  const targetEl = el || layerEl?.querySelector(`[data-view-window-id="${id}"]`);
  const win = findWindow(id);
  if (!win || !targetEl) return;
  deselectViewWindow();
  onBeforeSelect?.();
  selectedWindowId = id;
  targetEl.classList.add('is-selected');
  onSidebarRefreshNeeded?.();
  mountLiveInstance(win, targetEl);
}

function deselectViewWindow() {
  if (!selectedWindowId) return;
  const id = selectedWindowId;
  const win = findWindow(id);
  const el = layerEl?.querySelector(`[data-view-window-id="${id}"]`);
  const instance = liveInstance;
  const lifecycleGeneration = viewWindowLifecycleGeneration;
  selectedWindowId = null;
  onSidebarRefreshNeeded?.();
  if (win && instance) {
    // Camera/target are written synchronously by freezeLiveInstanceToSnapshot,
    // but PNG encoding finishes later. Invalidate the old bitmap now so a
    // tab/sheet switch that immediately serializes cannot persist a stale
    // image under the new camera pose; remount will regenerate it if needed.
    win.snapshot = null;
    win.snapshotUpdatedAt = null;
    win.snapshotGroupSelectionSignature = null;
    // freezeLiveInstanceToSnapshot()'s PNG encode is async and can take
    // ~1s — until it (and the teardown after it) finishes, the OLD canvas +
    // its OrbitControls stay fully attached and interactive. three_controls.js's
    // installCursorOrbit() binds a CAPTURE-phase pointerdown listener
    // directly on the canvas that calls stopImmediatePropagation() on any
    // hit against scene content, which silently swallows a click meant to
    // reselect this same window before it ever reaches this module's own
    // (bubble-phase) pointerdown handler on the window element — the click
    // has no visible effect, and only the window's OWN later, unrelated
    // teardown eventually downgrades it to a static snapshot. Reproduced via
    // rapid deselect+reselect cycling: the reselect click was silently
    // dropped every time, matching the reported "3D view often only loads
    // on the 2nd or 3rd try". Disabling pointer events on the stale canvas
    // immediately (before the async freeze even starts) routes any such
    // click straight to the window element instead, so a reselect attempt
    // during this window is handled normally rather than swallowed.
    instance.renderer.domElement.style.pointerEvents = 'none';
    freezeLiveInstanceToSnapshot(win).then(() => {
      teardownLiveInstance(instance);
      if (lifecycleGeneration !== viewWindowLifecycleGeneration) return;
      // The same window may have been reselected (a new live instance
      // already mounted, or mounting) while this freeze/teardown was still
      // in flight — a fast deselect+reselect click pair on the same window
      // easily outruns freezeLiveInstanceToSnapshot's render+PNG-encode
      // round trip. In that case, downgrading to a static snapshot here
      // would stomp on the freshly (re)mounted live canvas, and restoring
      // the pre-selection ambient state would undo that new instance's own
      // scene filters — this exact interleaving was reproducible via rapid
      // select/deselect cycling (every other cycle lost its live canvas
      // ~1s after re-selecting). The reselect's own eventual deselect
      // already owns all of this cleanup, so just skip it here.
      if (selectedWindowId === id) return;
      scheduleAmbientSceneRestore(instance.previousSceneFilters, instance.modules);
      el?.classList.remove('is-selected');
      if (win && el) renderWindowContent(win, el);
      onGeometryCommit?.();
    });
  } else {
    teardownLiveInstance(instance);
    el?.classList.remove('is-selected');
  }
}

// ---------------------------------------------------------------------------
// Per-window Ebenen/Beleuchtung setters (sidebar controls, only reachable
// while a window is selected — see selectedWindowSettingsHtml())
// ---------------------------------------------------------------------------

async function updateSelectedWindowSceneFilters(mutator) {
  const win = findWindow(selectedWindowId);
  if (!win || !win.sceneFilters) return;
  mutator(win.sceneFilters);
  onSidebarRefreshNeeded?.();
  onGeometryCommit?.();
  if (!liveInstance || liveInstance.id !== win.id) return; // still mounting, or nothing live
  await queueSceneWork(async () => {
    liveInstance.printGroupFilter?.restore?.();
    await applyWindowSceneFilters(win, liveInstance.modules, liveInstance.renderer);
    liveInstance.printGroupFilter = applyPrintPlanSceneGroupFilter(S.view3d?.projectRoot3D, {
      selectedGroupKeys: selectedPrintGroupKeys
    });
    liveInstance.printGroupSelectionSignature = selectedPrintGroupSignature;
    liveInstance.pseudoView3d.renderDirty = true;
    scheduleLiveFrame();
  });
}

function setSelectedWindowPreset(presetId) {
  if (!WINDOW_PRESET_IDS.includes(presetId)) return;
  updateSelectedWindowSceneFilters(sf => { sf.preset = presetId; });
}

function toggleSelectedWindowFilter(key) {
  if (!WINDOW_FILTER_KEYS.includes(key)) return;
  updateSelectedWindowSceneFilters(sf => { sf.filters[key] = !sf.filters[key]; });
}

function setSelectedWindowSunTime(time) {
  if (!isValidOutdoorSunTime(time)) return;
  updateSelectedWindowSceneFilters(sf => { sf.sunTime = time; });
}

// Live-preview only (input/drag) — pure lighting update, no rebuild, no
// persistence, no sidebar re-render (the slider itself already reflects the
// dragged value; re-rendering the panel mid-drag would rebuild/reset it).
function previewSelectedWindowBrightness(value) {
  const win = findWindow(selectedWindowId);
  if (!win || !win.sceneFilters || !liveInstance || liveInstance.id !== win.id) return;
  win.sceneFilters.brightness = normalizeOutdoorSunBrightness(value);
  applySceneLighting(win.sceneFilters, liveInstance.modules, liveInstance.renderer);
  liveInstance.pseudoView3d.renderDirty = true;
  scheduleLiveFrame();
}

// change event (drag released) — persist.
function commitSelectedWindowBrightness(value) {
  const win = findWindow(selectedWindowId);
  if (!win || !win.sceneFilters) return;
  win.sceneFilters.brightness = normalizeOutdoorSunBrightness(value);
  onGeometryCommit?.();
}

function isAnyViewWindowSelected() {
  return Boolean(selectedWindowId);
}

function setViewWindowSelectedGroupKeys(values, options = {}) {
  const normalized = normalizePrintPlanGroupKeys(values);
  const signature = printPlanGroupSelectionSignature(normalized);
  if (signature === selectedPrintGroupSignature) return false;
  selectedPrintGroupKeys = normalized;
  selectedPrintGroupSignature = signature;
  if (options.invalidate === false) return true;

  viewWindowLifecycleGeneration += 1;
  const lifecycleGeneration = viewWindowLifecycleGeneration;
  windowsState.forEach(win => {
    win.snapshot = null;
    win.snapshotUpdatedAt = null;
    win.snapshotGroupSelectionSignature = null;
    const el = layerEl?.querySelector(`[data-view-window-id="${win.id}"]`);
    if (el && !isSelected(win.id)) renderWindowContent(win, el);
    if (el && !isSelected(win.id)) {
      queueSceneWork(() => generateInitialSnapshot(win, el))
        .catch(error => console.error('3D-Fenster konnte nach Gruppenwechsel nicht aktualisiert werden', error));
    }
  });

  const selectedWin = findWindow(selectedWindowId);
  const selectedEl = layerEl?.querySelector(`[data-view-window-id="${selectedWindowId}"]`);
  const instance = liveInstance;
  if (selectedWin && selectedEl && !instance) {
    mountLiveInstance(selectedWin, selectedEl);
  } else if (selectedWin && instance?.id === selectedWin.id) {
    const selection = currentPrintGroupSelection();
    queueSceneWork(async () => {
      instance.printGroupFilter?.restore?.();
      await applyWindowSceneFilters(selectedWin, instance.modules, instance.renderer);
      const printGroupFilter = applyPrintPlanSceneGroupFilter(S.view3d?.projectRoot3D, {
        selectedGroupKeys: selection.selectedGroupKeys
      });
      if (instance.disposed
        || instance !== liveInstance
        || lifecycleGeneration !== viewWindowLifecycleGeneration
        || selection.signature !== selectedPrintGroupSignature) {
        printGroupFilter.restore();
        return;
      }
      instance.printGroupFilter = printGroupFilter;
      instance.printGroupSelectionSignature = selection.signature;
      instance.pseudoView3d.renderDirty = true;
      scheduleLiveFrame();
    }).catch(error => console.error('Live-3D-Fenster konnte nach Gruppenwechsel nicht aktualisiert werden', error));
  }
  onGeometryCommit?.();
  return true;
}

// ---------------------------------------------------------------------------
// Mount / lifecycle (called from print_plan_modal.js)
// ---------------------------------------------------------------------------

function mountViewWindows(layer, savedWindows, callbacks = {}) {
  viewWindowLifecycleGeneration += 1;
  selectedPrintGroupKeys = normalizePrintPlanGroupKeys(callbacks.selectedGroupKeys);
  selectedPrintGroupSignature = printPlanGroupSelectionSignature(selectedPrintGroupKeys);
  layerEl = layer;
  snapGuideVerticalEl = null;
  snapGuideHorizontalEl = null;
  windowsState = normalizeViewWindows(savedWindows);
  onGeometryCommit = typeof callbacks.onGeometryCommit === 'function' ? callbacks.onGeometryCommit : null;
  onSidebarRefreshNeeded = typeof callbacks.onSidebarRefreshNeeded === 'function' ? callbacks.onSidebarRefreshNeeded : null;
  onBeforeSelect = typeof callbacks.onBeforeSelect === 'function' ? callbacks.onBeforeSelect : null;
  selectedWindowId = null;
  if (layerEl) {
    layerEl.innerHTML = '';
    windowsState.forEach(win => {
      if (!snapshotMatchesPrintGroupSelection(win)) {
        win.snapshot = null;
        win.snapshotUpdatedAt = null;
        win.snapshotGroupSelectionSignature = null;
      }
      const el = buildWindowElement(win);
      layerEl.appendChild(el);
      if (!win.snapshot) {
        queueSceneWork(() => generateInitialSnapshot(win, el))
          .catch(error => console.error('3D-Fenster konnte beim Laden nicht aktualisiert werden', error));
      }
    });
  }
}

function disposeViewWindows() {
  viewWindowLifecycleGeneration += 1;
  deselectViewWindow();
  teardownLiveInstance();
  // Also invalidate the generation captured by deselectViewWindow(): its
  // asynchronous PNG freeze must not restore an ambient scene after unmount.
  viewWindowLifecycleGeneration += 1;
  layerEl = null;
  snapGuideVerticalEl = null;
  snapGuideHorizontalEl = null;
  windowsState = [];
  onGeometryCommit = null;
  onSidebarRefreshNeeded = null;
  onBeforeSelect = null;
}

// ---------------------------------------------------------------------------
// Print-time bake: one fresh, disposable, high-resolution render per window
// ---------------------------------------------------------------------------

const PRINT_WINDOW_SNAPSHOT_DPI = 200;
const MM_PER_INCH = 25.4;

// Shared by renderWindowForPrint() (full-resolution, for the actual print
// bake) and renderPlaceholderSnapshot() (small, for the editor placeholder
// image of a freshly-created window) — same fresh/disposable-renderer
// approach, just a different target resolution.
async function renderWindowSnapshotAtSize(win, width, height, selection = currentPrintGroupSelection()) {
  const modules = await load3DModules();
  const THREE = await modules.scene3d.ensure3D();
  const sceneFilters = await applyWindowSceneFilters(win, modules, null);
  const scene = S.view3d.scene;
  // Filtering must precede default-pose calculation so a new print window is
  // framed to the selected groups rather than the complete project.
  const printGroupFilter = applyPrintPlanSceneGroupFilter(S.view3d?.projectRoot3D, {
    selectedGroupKeys: selection.selectedGroupKeys
  });
  const aspect = width / height;
  const defaultPose = defaultCameraPose(modules.scene3d, THREE, selection);
  const pose = (win.camera.position && win.camera.target) ? win.camera : { ...defaultPose, projectionMode: win.camera.projectionMode, orthographicViewHeight: win.camera.orthographicViewHeight };
  const camera = modules.scene3d.create3DCamera(THREE, win.camera.projectionMode, aspect, { viewHeight: pose.orthographicViewHeight || undefined });
  const posVec = pose.position || defaultPose.position;
  const targetVec = pose.target || defaultPose.target;
  camera.position.set(posVec.x, posVec.y, posVec.z);
  camera.up.set(0, 1, 0);
  camera.lookAt(targetVec.x, targetVec.y, targetVec.z);

  let renderer = null;
  const previousSceneBackground = scene.background;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setClearColor(0x000000, 0);
    renderer.setSize(width, height, false);
    // Re-applied with the real renderer now that it exists (see the analogous
    // comment in mountLiveInstance()) — targets this renderer's own tone-mapping
    // exposure specifically.
    applySceneLighting(sceneFilters, modules, renderer);
    // scene.background belongs to the shared S.view3d.scene — blank it just
    // for this one render so the baked PNG has no background color at all
    // (transparent, so the print page shows through), then restore it since
    // other code (incl. the main 3D modal) reads this same scene object.
    scene.background = null;
    renderer.render(scene, camera);
    return renderer.domElement.toDataURL('image/png');
  } finally {
    printGroupFilter.restore();
    scene.background = previousSceneBackground;
    releaseWebGLRenderer(renderer);
  }
}

// Sicherheits-Obergrenze für die Druck-Kantenlänge in Pixeln — echter Bug-
// Fund (siehe dieselbe Konstruktion in print_plan_2d_views.js's
// renderWindowForPrint()): bei 200 DPI ergibt bereits ein paar Meter Fenster-
// Breite eine Zielgrösse weit jenseits von gl.MAX_TEXTURE_SIZE (in dieser
// Session gemessen: 8192) — der Renderer bekam dadurch eine vom Treiber gar
// nicht unterstützte Canvas-Grösse, wodurch der Bake nie fertig wurde (im
// echten Druck: leerer Fensterrahmen ohne Bild). EIN gemeinsamer, einmalig
// abgefragter Skalierungsfaktor auf beide Kanten (Seitenverhältnis bleibt
// exakt erhalten) degradiert die effektive DPI nur für unüblich grosse
// Projekte, statt den kompletten Druck fehlschlagen zu lassen.
let maxRenderEdgePxPromise = null;
function queryMaxRenderEdgePx() {
  if (!maxRenderEdgePxPromise) {
    maxRenderEdgePxPromise = maxWebGLRenderEdgePx(S.view3d?.renderer);
  }
  return maxRenderEdgePxPromise;
}

async function renderWindowForPrint(win, selection = currentPrintGroupSelection()) {
  const modules = await load3DModules();
  await modules.scene3d.ensure3D();
  const maxEdgePx = await queryMaxRenderEdgePx();
  const rawWidth = (win.widthMm / MM_PER_INCH) * PRINT_WINDOW_SNAPSHOT_DPI;
  const rawHeight = (win.heightMm / MM_PER_INCH) * PRINT_WINDOW_SNAPSHOT_DPI;
  const maxEdge = Math.max(rawWidth, rawHeight);
  const scale = maxEdge > maxEdgePx ? maxEdgePx / maxEdge : 1;
  const width = Math.max(1, Math.round(rawWidth * scale));
  const height = Math.max(1, Math.round(rawHeight * scale));
  return renderWindowSnapshotAtSize(win, width, height, selection);
}

function renderPlaceholderSnapshot(win, selection = currentPrintGroupSelection()) {
  const aspect = win.widthMm / Math.max(1, win.heightMm);
  const width = aspect >= 1 ? PLACEHOLDER_SNAPSHOT_MAX_EDGE_PX : Math.round(PLACEHOLDER_SNAPSHOT_MAX_EDGE_PX * aspect);
  const height = aspect >= 1 ? Math.round(PLACEHOLDER_SNAPSHOT_MAX_EDGE_PX / aspect) : PLACEHOLDER_SNAPSHOT_MAX_EDGE_PX;
  return renderWindowSnapshotAtSize(win, Math.max(1, width), Math.max(1, height), selection);
}

// Every window is baked as a static <img>, never the live canvas — matches
// this modal's own established lesson that live/interactive content is
// unreliable in the real browser print pipeline (see the foreignObject
// comments in print_plan_modal.js), independent of whether the window
// happens to be the currently-selected/live one in the editor.
//
// Rendered ONE AT A TIME (not Promise.all) — spinning up several
// WebGLRenderers concurrently was hitting the browser's limit on
// simultaneous WebGL contexts ("Too many active WebGL contexts. Oldest
// context will be lost."), silently corrupting whichever renderer's context
// got evicted. Awaiting + disposing each renderer before starting the next
// keeps at most one extra context alive at a time.
async function viewWindowsPrintHtml(borderMm) {
  if (!windowsState.length) return '';
  const images = [];
  const modules = await load3DModules();
  // Captured ONCE, before any window's bake mutates S.view3d — re-reading
  // current3DModalScenePreset()/normalize3DModalFilters() after the loop
  // would just reflect whichever window was baked LAST, not the true
  // pre-print state.
  const ambientBaseline = captureAmbientSceneFilters(modules);
  const selection = currentPrintGroupSelection();
  for (const win of windowsState) {
    try {
      const dataUrl = await queueSceneWork(() => renderWindowForPrint(win, selection));
      images.push(`<img style="position:absolute;left:${win.xMm}mm;top:${win.zMm}mm;width:${win.widthMm}mm;height:${win.heightMm}mm;object-fit:cover;box-sizing:border-box;border:${borderMm}mm solid #000" src="${dataUrl}">`);
    } catch (error) {
      console.error('3D-Fenster konnte nicht für den Druck gerendert werden', error);
    }
  }
  // Always leave the shared scene in a sane, consistent state after baking —
  // the currently-live window's own settings if one is selected, otherwise
  // the true pre-print ambient baseline. Without this, S.view3d.modalScenePreset/
  // .modalFilters and the shared lights would silently keep showing whatever
  // the LAST baked window's preset happened to be, even with nothing selected.
  await queueSceneWork(async () => {
    if (liveInstance) {
      const win = findWindow(liveInstance.id);
      if (win) {
        liveInstance.printGroupFilter?.restore?.();
        await applyWindowSceneFilters(win, liveInstance.modules, liveInstance.renderer);
        liveInstance.printGroupFilter = applyPrintPlanSceneGroupFilter(S.view3d?.projectRoot3D, {
          selectedGroupKeys: selectedPrintGroupKeys
        });
        liveInstance.pseudoView3d.renderDirty = true;
        scheduleLiveFrame();
      }
    } else {
      await applyAmbientSceneState(ambientBaseline, modules);
      applySceneLighting(ambientBaseline, modules, null);
    }
  });
  return images.join('');
}

export {
  normalizeViewWindows,
  serializeViewWindows,
  viewsSidebarCardHtml,
  mountViewWindows,
  disposeViewWindows,
  syncViewWindowsGeometry,
  selectViewWindow,
  deselectViewWindow,
  isAnyViewWindowSelected,
  addViewWindow,
  viewWindowsPrintHtml,
  setSelectedWindowPreset,
  toggleSelectedWindowFilter,
  setSelectedWindowSunTime,
  setViewWindowSelectedGroupKeys,
  previewSelectedWindowBrightness,
  commitSelectedWindowBrightness,
  queueSceneWork
};
